import { Box, Stack } from "@mui/material";
import { useContactDrawer } from "components/Contact/useContactDrawer";
import PropTypes from "prop-types";
import React, { useState, useMemo, useCallback } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { getFocus, useAtom, useGet } from "state/jotai";
import { active_project, current_language, params_query, previousUrl } from "state/store.global";
import { animateScroll } from "react-scroll";
import useUnitPage from "./useUnitPage";
import { useBreak } from "hooks/useBreak";
import styled, { css } from "styled-components";
import { toggle_drawer } from "state/actions";
import { _project } from "state/store.projects";
import { darken } from "polished";
import { useBookViewingDrawer } from "components/Contact/useBookViewingDrawer";
import { useSendClick } from "hooks/fetch/useSendClick";

function NewReservationButton({ unit}) {
  const { open: bookViweingOpen } = useBookViewingDrawer();
  const location = useLocation();
  const here = location.pathname;
  const atUnit = here.includes("unit");
  const [refContainer] = useUnitPage();
  const isDesktop = useBreak("md_up");
  const sendClick = useSendClick();
  const { open } = useContactDrawer();
  const navigateTo = useNavigate();
  const params = useGet(params_query);
  const { pin, pid } = useGet(active_project);
  const buttonsLabel = getFocus(_project, "buttonsLabel")?.[pin]?.[pid] ?? [];

  const language = useAtom(current_language)[0];
  const [, toggleDrawer] = useAtom(toggle_drawer);
  const [, setPreviousUrl] = useAtom(previousUrl);

  const [selectedButton, setSelectedButton] = useState(
    atUnit ? "contactUs" : "readMore"
  );

  // Utility function to blur all buttons
  const blurAllButtons = useCallback(() => {
    const buttons = document.querySelectorAll("button");
    buttons.forEach((button) => button.blur());
  }, []);

  // Memoized button labels to avoid repeated filtering
  const buttonLabels = useMemo(() => {
    const readMoreLabel = buttonsLabel?.find(
      (btn) => btn?.UILabelProperty === "Læsmereomenhed"
    )?.Label;
    
    const contactLabel = buttonsLabel?.find(
      (btn) => btn?.UILabelProperty === "kontaktOs"
    )?.Label;

    return { readMoreLabel, contactLabel };
  }, [buttonsLabel]);

  const handleMore = useCallback((e) => {
    sendClick("unit_know_more_button", { unitID: unit?.data?.UnitID });
    blurAllButtons();
    setPreviousUrl(location.pathname);
    navigateTo(
      `/${language}/unit/${unit.getId()}${params.length ? params : ""}`
    );
    toggleDrawer();
    setSelectedButton("readMore");
  }, [blurAllButtons, setPreviousUrl, location.pathname, navigateTo, language, unit, params, toggleDrawer]);

  const handleClick = useCallback((e) => {
    blurAllButtons();
    const targetElement = document.getElementById("unit_page_contact_form");
  sendClick("unit_ext_contact_button", { unitID: unit?.data?.UnitID });
    if (isDesktop && targetElement) {
      refContainer.scrollTo(0, targetElement.offsetTop);
    } else if (targetElement) {
      const offset = targetElement.offsetTop;
      animateScroll.scrollTo(offset, { container: refContainer });
    }
  }, [blurAllButtons, isDesktop, refContainer]);

  const handleContact = useCallback((e) => {
      sendClick("unit_contact_button", { unitID: unit?.data?.UnitID });
    blurAllButtons();
    open(unit);
    setSelectedButton("contactUs");
  }, [blurAllButtons, open, unit]);

  const handleBookView = useCallback(() => {
    blurAllButtons();
    bookViweingOpen(unit);
  }, [blurAllButtons, bookViweingOpen, unit]);

  return (
    <Box sx={{ padding: atUnit ? "" : "24px 32px" }}>
      <Stack spacing={2}>
        {!atUnit && (
          <CustomButton
            variant="primary"
            onClick={handleMore}
            clr={clr}
          >
            <span className="eus-button-2">
              {buttonLabels.readMoreLabel}
            </span>
          </CustomButton>
        )}
        <CustomButton
          variant={selectedButton === "contactUs" && atUnit ? "primary" : "secondary"}
          onClick={atUnit ? handleClick : handleContact}
          atUnit={atUnit}
          clr={clr}
        >
          <span className="eus-button-2">
            {buttonLabels.contactLabel}
          </span>
        </CustomButton>
      </Stack>
    </Box>
  );
}

export default NewReservationButton;

NewReservationButton.propTypes = {
  unit: PropTypes.object.isRequired,
  clr: PropTypes.object
};

  // Custom styled button component
export const CustomButton = styled.button`
  padding      : ${({ padding }) => (padding ? padding : "12px 40px")};
  border-radius: 6px;
  cursor       : ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  box-sizing   : border-box;
  height       : 44px;
  opacity      : ${({ disabled }) => (disabled ? 0.5 : 1)};

  color: ${({ variant, atUnit, clr }) => {
    if (atUnit) {
      return clr?.buttons_text || clr?.primary_text;
    }
    return variant === "primary" ? "white" : "black";
  }};

  background-color: ${({ variant, clr }) =>
    variant === "primary" ? `${clr?.buttons}` : clr?.buttons_text};

  border: ${({ variant }) =>
    variant === "primary" ? `1px solid ${clr?.buttons}` : "1px solid #D1D1D1"};

  transition: background-color 0.1s ease-in, border 0.1s ease-in,
    box-shadow 0.1s ease-in;

  &:hover {
    background-color: ${({ variant, clr, disabled }) => {
      if (disabled) {
        return "";  // Disabled hover background
      }

      if (variant === "primary") {
        return darken(0.05, clr?.buttons);
      }
        // if (disabled) {
        //   return ``;
        // }
      return   darken(0.05, clr?.buttons_text);
    }};

    border: ${({ variant }) =>
      variant === "primary"
        ? `1px solid ${clr?.buttons}`
        : "1px solid #D1D1D1"};
  }

  &:focus {
    background-color: ${({ variant, clr, disabled }) => {
      if (disabled) {
        return " ";  // Disabled focus background
      }
      if (variant === "primary") {
        return darken(0.05, clr?.buttons);
      }
      return clr?.buttons_text;
    }};
    box-shadow: ${({ variant }) =>
      variant === "secondary" ? "0 0 0 1.5px #D1D1D1" : `0 0 0 1.5px ${clr?.buttons}`};
  }
  &:disabled {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  }
`;

